import axios from "axios";
import { AuthActionCreators, IGet, IPost } from "@nkr/store/authorization";
import { IMicrositeEventLog } from "@nkr/models/index";
import { useAppHookContext } from "@nkr/utils/rest-api";
import { useEffect, useState } from "react";
import { CoachListTab } from "@nkr/enums/index";

export { CoachListTab };

export interface ICoachFilteredListRequest {
    coachContactId: number;
    tab: CoachListTab;
    organType?: number;
    languagePreference?: number;
    messageStatus?: number;
    centerId?: number;
    coachStatus?: number;
    status?: number;
    search?: string;
    showHidden?: boolean;
    sortBy?: string;
    sortDescending?: boolean;
    page?: number;
    pageSize?: number;
}

export interface ICoachFilteredListItem {
    id: number;
    commStatus: number;
    hideFromCoach: boolean;
    micrositeType: string;
    coachStatusDescription: string;
    centerAlias: string;
    firstName: string;
    lastName: string;
    birthdate: string | null;
    age: number;
    languagePreference: string;
    starterSiteActiveDate: string | null;
    customSiteApprovalWaitDays: number | null;
    customSiteApprovalOwner: string | null;
    currentStatusDescription: string;
    customSiteStatusId: number;
    mostRecentCoachContactDateTime: string | null;
    nextFollowupDate: string | null;
    coachFullName: string | null;
    shortCode: string;
    email: string | null;
    businessCardsRequestDate: string | null;
    voucherOrTransplantEvents: IMicrositeEventLog[];
    micrositeBannerStatusId: number;
    micrositeBannerStatusDesc: string;
}

interface IRetVal {
    loading: boolean;
    error: string;
    items: ICoachFilteredListItem[];
    total: number;
    toggleHide: (item: ICoachFilteredListItem) => Promise<void>;
    updateItem: (id: number, patch: Partial<ICoachFilteredListItem>) => void;
}

const useCoachFilteredList = (request: ICoachFilteredListRequest, refreshKey: number = 0): IRetVal => {
    const {
        coachContactId, tab, organType, languagePreference, messageStatus,
        centerId, coachStatus, status, search, showHidden,
        sortBy, sortDescending, page, pageSize
    } = request;

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [items, setItems] = useState<ICoachFilteredListItem[]>([]);
    const [total, setTotal] = useState<number>(0);

    const { dispatch } = useAppHookContext();

    useEffect(() => {
        if (!coachContactId) {
            return;
        }

        setLoading(true);
        setError('');

        const source = axios.CancelToken.source();

        const fetchData = async () => {
            const params = new URLSearchParams({
                coachContactId: String(coachContactId),
                tab: String(tab),
                page: String(page ?? 1),
                pageSize: String(pageSize ?? 25)
            });

            if (organType != null) params.append('organType', String(organType));
            if (languagePreference != null) params.append('languagePreference', String(languagePreference));
            if (messageStatus != null) params.append('messageStatus', String(messageStatus));
            if (centerId != null) params.append('centerId', String(centerId));
            if (coachStatus != null) params.append('coachStatus', String(coachStatus));
            if (status != null) params.append('status', String(status));
            if (search) params.append('search', search);
            if (showHidden) params.append('showHidden', 'true');
            if (sortBy) params.append('sortBy', sortBy);
            if (sortDescending) params.append('sortDescending', 'true');

            const config: IGet = {
                url: `${__API__}/microsite/coach/filtered-list?${params.toString()}`,
                cancelToken: source.token,
            };

            let cancelled = false;
            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.success) {
                    setItems(response.data.results.items);
                    setTotal(response.data.results.total);
                } else {
                    setError('Unable to load list');
                    setItems([]);
                    setTotal(0);
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    cancelled = true;
                    return;
                }
                setItems([]);
                setTotal(0);
                setError(err?.message ?? 'An error occurred');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => source.cancel();
    }, [
        coachContactId, tab, organType, languagePreference, messageStatus,
        centerId, coachStatus, status, search, showHidden,
        sortBy, sortDescending, page, pageSize, refreshKey
    ]);

    const toggleHide = async (item: ICoachFilteredListItem) => {
        const newHide = !item.hideFromCoach;

        const config: IPost = { url: `${__API__}/microsite/coach/hide-microsite/${item.id}/${newHide}` };

        try {
            await dispatch(AuthActionCreators.apiPost(config));
            if (!showHidden && newHide) {
                setItems(prev => prev.filter(x => x.id !== item.id));
                setTotal(prev => prev - 1);
            } else {
                setItems(prev => prev.map(x => x.id === item.id ? { ...x, hideFromCoach: newHide } : x));
            }
        } catch {
            // leave list unchanged on failure
        }
    };

    const updateItem = (id: number, patch: Partial<ICoachFilteredListItem>) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    };

    return { loading, error, items, total, toggleHide, updateItem };
}

export default useCoachFilteredList;
